import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, Paper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventLogTab from '@features/scripts/components/execution/EventLogTab';
import type { ExecutionEvent } from '@features/scripts/hooks/useExecutionStatus';

type Props = {
  events: ExecutionEvent[];
};

const TICKER_COUNT = 3;

const EVENT_COLORS: Record<ExecutionEvent['type'], string> = {
  info: 'text.primary',
  warning: 'warning.main',
  error: 'error.main',
  success: 'success.main'
};

const formatTime = (isoString: string): string =>
  new Date(isoString).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

const RobotTicker: React.FC<Props> = ({ events }) => {
  const { t } = useTranslation('calibration');
  const [expanded, setExpanded] = useState(false);

  const recent = events.slice(-TICKER_COUNT);

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.5,
          gap: 1
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t('runPage.robotActivity', { defaultValue: 'Robot activity' })}
        </Typography>
        <Button
          size="small"
          onClick={() => setExpanded(prev => !prev)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {t('runPage.fullLog', { defaultValue: 'Full log' })}
        </Button>
      </Box>

      {!expanded && (
        <Box
          sx={{
            px: 1.5,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25
          }}
        >
          {recent.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              {t('runPage.noEventsYet', {
                defaultValue: 'Waiting for robot events…'
              })}
            </Typography>
          ) : (
            recent.map((event, i) => (
              <Box
                key={`${event.timestamp}-${i}`}
                sx={{ display: 'flex', gap: 1, minWidth: 0 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace', flexShrink: 0 }}
                >
                  {formatTime(event.timestamp)}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ fontFamily: 'monospace', color: EVENT_COLORS[event.type] }}
                >
                  {event.message}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}

      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            height: 240,
            borderTop: theme => `1px solid ${theme.palette.divider}`
          }}
        >
          <EventLogTab events={events} />
        </Box>
      </Collapse>
    </Paper>
  );
};

export default RobotTicker;
